import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiFetch, assetUrl } from "../utils/api";
import WeChatLoginButton from "../components/WeChatLoginButton";
import GratitudePlanetMapPromo from "./GratitudePlanetMapPromo";
import { paintGratitudeCardAsync } from "../utils/gratitudeCardPaint";
import { compressImageFile } from "../utils/compressImageFile";

const MIN_ITEMS = 3;
const MAX_ITEMS = 10;
const MAX_ITEM_LEN = 500;
const MAX_IMAGES = 3;
const MAX_COMMENT_LEN = 200;

function emptyItems(n = MIN_ITEMS) {
  return Array.from({ length: n }, () => "");
}

function AuthorAvatar({ src, name, className = "w-11 h-11" }) {
  const [broken, setBroken] = useState(false);
  const url = assetUrl(src || "");
  const letter = (name || "?").trim().slice(0, 1) || "?";
  useEffect(() => {
    setBroken(false);
  }, [url]);
  if (!url || broken) {
    return (
      <span
        className={`${className} rounded-full border shrink-0 bg-amber-100 text-amber-800 inline-flex items-center justify-center text-sm font-semibold`}
        title={name || ""}
        aria-hidden
      >
        {letter}
      </span>
    );
  }
  return (
    <img
      src={url}
      alt={name || ""}
      title={name || ""}
      referrerPolicy="no-referrer"
      className={`${className} rounded-full object-cover border shrink-0 bg-amber-50`}
      onError={() => setBroken(true)}
    />
  );
}

function ImageGrid({ urls, onRemove }) {
  if (!urls?.length) return null;
  return (
    <div className="grid grid-cols-3 gap-2">
      {urls.map((url, idx) => (
        <div key={`${url}-${idx}`} className="relative aspect-square">
          <img
            src={assetUrl(url)}
            alt=""
            className="w-full h-full object-cover rounded-lg border border-amber-50"
          />
          {onRemove ? (
            <button
              type="button"
              onClick={() => onRemove(idx)}
              className="absolute top-1 right-1 w-5 h-5 bg-black/50 text-white rounded-full text-xs leading-none"
              aria-label="移除图片"
            >
              ×
            </button>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function ActionIcon({ label, active, onClick, children, count }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 text-xs px-1.5 py-1 rounded-lg transition ${
        active ? "text-rose-600" : "text-gray-500 hover:text-gray-700"
      }`}
      title={label}
      aria-label={label}
    >
      {children}
      {count != null && Number(count) > 0 ? (
        <span className="tabular-nums">{count}</span>
      ) : null}
    </button>
  );
}

export default function BookClubGratitudeBoard() {
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const fileRef = useRef(null);
  const cardCanvasRef = useRef(null);
  const userId = Number(localStorage.getItem("user_id") || 0);
  const [feed, setFeed] = useState([]);
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState(emptyItems(MIN_ITEMS));
  const [images, setImages] = useState([]);
  const [mine, setMine] = useState(null);
  const [displayNameInput, setDisplayNameInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [nameBusy, setNameBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const [justPosted, setJustPosted] = useState(false);
  const [editing, setEditing] = useState(false);
  const [openComments, setOpenComments] = useState({});
  const [commentDrafts, setCommentDrafts] = useState({});
  const [commentBusy, setCommentBusy] = useState({});
  const [cardPost, setCardPost] = useState(null);
  const [cardReady, setCardReady] = useState(false);
  const [cardImageUrl, setCardImageUrl] = useState("");
  const [cardHint, setCardHint] = useState("");
  /** all | mine */
  const [feedScope, setFeedScope] = useState("all");

  const maxItems = meta?.max_items || MAX_ITEMS;
  const maxItemLen = meta?.max_item_len || MAX_ITEM_LEN;
  const maxImages = meta?.max_images || MAX_IMAGES;
  const maxCommentLen = meta?.max_comment_len || MAX_COMMENT_LEN;

  const loginRedirect = `/login?redirect=${encodeURIComponent("/book-club?tab=gratitude")}`;

  const requireLogin = () => {
    if (userId) return true;
    navigate(loginRedirect);
    return false;
  };

  const patchFeedPost = (postId, patch) => {
    setFeed((prev) =>
      prev.map((p) => (p.id === postId ? { ...p, ...patch } : p))
    );
  };

  const loadFeed = async (scope = feedScope) => {
    try {
      const parts = ["page=1", "page_size=30"];
      if (userId) parts.push(`user_id=${userId}`);
      if (scope === "mine" && userId) parts.push("mine=1");
      const res = await apiFetch(
        `/api/book-club/gratitude/feed?${parts.join("&")}`
      );
      const data = await res.json();
      if (res.ok) {
        setFeed(data.items || []);
        setMeta(data.meta || null);
      }
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  const applyMineItem = (data) => {
    setMine(data);
    const name = data.display_name || "";
    setDisplayNameInput(name);
    if (name) localStorage.setItem("name", name);
    if (data.display_avatar) localStorage.setItem("avatar", data.display_avatar);
    if (data.item?.items?.length) {
      const next = [...data.item.items];
      while (next.length < MIN_ITEMS) next.push("");
      setItems(next.slice(0, maxItems));
      setImages(
        Array.isArray(data.item.images) ? data.item.images.slice(0, maxImages) : []
      );
      setEditing(false);
    } else {
      setEditing(true);
      setImages([]);
    }
  };

  const loadMine = async () => {
    if (!userId) {
      setMine(null);
      return;
    }
    try {
      const res = await apiFetch(`/api/book-club/gratitude/mine?user_id=${userId}`);
      const data = await res.json();
      if (res.ok) applyMineItem(data);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    setLoading(true);
    loadFeed(feedScope);
    loadMine();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, feedScope]);

  const switchFeedScope = (scope) => {
    if (scope === "mine" && !userId) {
      navigate(loginRedirect);
      return;
    }
    setFeedScope(scope);
  };

  useEffect(() => {
    if (!cardPost || !cardCanvasRef.current) return;
    let cancelled = false;
    setCardReady(false);
    setCardImageUrl("");
    setCardHint("手机请长按上方图片，选择「保存到相册」");
    (async () => {
      await paintGratitudeCardAsync(cardCanvasRef.current, {
        items: cardPost.items || [],
        authorName: cardPost.author_name || "",
        authorAvatarUrl: assetUrl(cardPost.author_avatar || ""),
        checkinDay: cardPost.checkin_day || 1,
        postDate: cardPost.post_date || "",
        pageUrl: `${window.location.origin}/book-club`,
      });
      if (cancelled) return;
      const url = cardCanvasRef.current.toDataURL("image/png");
      setCardImageUrl(url);
      setCardReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [cardPost]);

  const updateItem = (idx, value) => {
    setItems((prev) =>
      prev.map((v, i) => (i === idx ? value.slice(0, maxItemLen) : v))
    );
  };

  const addSlot = () => {
    if (items.length >= maxItems) return;
    setItems((prev) => [...prev, ""]);
  };

  const removeSlot = (idx) => {
    if (items.length <= MIN_ITEMS) return;
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const saveName = async () => {
    if (!userId) return;
    setNameBusy(true);
    setMsg("");
    try {
      const res = await apiFetch("/api/book-club/gratitude/display-name", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
          display_name: displayNameInput.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          typeof data.detail === "string" ? data.detail : data.message || "保存失败"
        );
      }
      if (data.display_name) localStorage.setItem("name", data.display_name);
      setMsg(data.message || "姓名已保存");
      await loadMine();
      await loadFeed();
    } catch (e) {
      setMsg(e.message || "保存姓名失败");
    } finally {
      setNameBusy(false);
    }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !userId) return;
    if (images.length + files.length > maxImages) {
      setMsg(`最多上传 ${maxImages} 张图片`);
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setUploading(true);
    setMsg("");
    try {
      for (const file of files) {
        const compressed = await compressImageFile(file, {
          maxEdge: 1920,
          maxBytes: 2.5 * 1024 * 1024,
        });
        if (compressed.size > 8 * 1024 * 1024) {
          throw new Error("图片过大，压缩后仍超过 8MB，请换一张较小的图");
        }
        const form = new FormData();
        form.append("user_id", String(userId));
        form.append("file", compressed);
        const res = await apiFetch("/api/book-club/gratitude/upload-image", {
          method: "POST",
          body: form,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.url) {
          throw new Error(
            typeof data.detail === "string" ? data.detail : "图片上传失败"
          );
        }
        setImages((prev) => [...prev, data.url].slice(0, maxImages));
      }
    } catch (err) {
      setMsg(err.message || "图片上传失败");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async () => {
    if (!userId) {
      navigate(loginRedirect);
      return;
    }
    if (!displayNameInput.trim()) {
      setMsg("请先填写姓名，方便大家认出你");
      return;
    }
    setBusy(true);
    setMsg("");
    try {
      const res = await apiFetch("/api/book-club/gratitude", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
          items,
          images,
          display_name: displayNameInput.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          typeof data.detail === "string" ? data.detail : data.message || "提交失败"
        );
      }
      setMsg(data.message || "打卡成功");
      setJustPosted(true);
      setEditing(false);
      if (data.display_name) localStorage.setItem("name", data.display_name);
      await loadFeed();
      await loadMine();
    } catch (e) {
      setMsg(e.message || "提交失败");
    } finally {
      setBusy(false);
    }
  };

  const startEdit = () => {
    setEditing(true);
    if (mine?.item) {
      const next = [...(mine.item.items || [])];
      while (next.length < MIN_ITEMS) next.push("");
      setItems(next.slice(0, maxItems));
      setImages(Array.isArray(mine.item.images) ? [...mine.item.images] : []);
    }
    setTimeout(() => {
      editorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const toggleLike = async (post) => {
    if (!requireLogin()) return;
    try {
      const res = await apiFetch(`/api/book-club/gratitude/${post.id}/like`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(typeof data.detail === "string" ? data.detail : "点赞失败");
      }
      patchFeedPost(post.id, {
        liked_by_me: data.liked,
        like_count: data.like_count,
        recent_likers: data.recent_likers || [],
      });
    } catch (e) {
      setMsg(e.message || "点赞失败");
    }
  };

  const loadComments = async (postId) => {
    try {
      const res = await apiFetch(
        `/api/book-club/gratitude/${postId}/comments?page=1&page_size=50`
      );
      const data = await res.json();
      if (res.ok) {
        setOpenComments((prev) => ({
          ...prev,
          [postId]: { open: true, items: data.items || [] },
        }));
      }
    } catch {
      /* ignore */
    }
  };

  const toggleComments = async (post) => {
    const cur = openComments[post.id];
    if (cur?.open) {
      setOpenComments((prev) => ({
        ...prev,
        [post.id]: { ...cur, open: false },
      }));
      return;
    }
    await loadComments(post.id);
  };

  const submitComment = async (postId) => {
    if (!requireLogin()) return;
    const text = (commentDrafts[postId] || "").trim();
    if (!text) return;
    setCommentBusy((prev) => ({ ...prev, [postId]: true }));
    try {
      const res = await apiFetch(`/api/book-club/gratitude/${postId}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId, content: text }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(typeof data.detail === "string" ? data.detail : "回复失败");
      }
      setCommentDrafts((prev) => ({ ...prev, [postId]: "" }));
      patchFeedPost(postId, { reply_count: data.reply_count });
      setOpenComments((prev) => {
        const cur = prev[postId] || { open: true, items: [] };
        return {
          ...prev,
          [postId]: {
            open: true,
            items: [...(cur.items || []), data.item].filter(Boolean),
          },
        };
      });
    } catch (e) {
      setMsg(e.message || "回复失败");
    } finally {
      setCommentBusy((prev) => ({ ...prev, [postId]: false }));
    }
  };

  const sharePost = async (post) => {
    if (!requireLogin()) return;
    const url = `${window.location.origin}/book-club?tab=gratitude#gratitude-post-${post.id}`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: `${post.author_name}的感恩打卡`,
          text: (post.items || []).slice(0, 3).join("；"),
          url,
        });
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        setMsg("链接已复制，可以转发分享");
      } else {
        window.prompt("复制链接转发", url);
      }
      const res = await apiFetch(`/api/book-club/gratitude/${post.id}/share`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        patchFeedPost(post.id, { share_count: data.share_count });
      }
    } catch (e) {
      if (e?.name === "AbortError") return;
      setMsg(e.message || "转发失败");
    }
  };

  const closeCardModal = () => {
    setCardPost(null);
    setCardImageUrl("");
    setCardHint("");
    setCardReady(false);
  };

  const downloadCard = async () => {
    const canvas = cardCanvasRef.current;
    if (!canvas || !cardReady) return;
    setCardHint("");

    // 手机优先：系统分享/存图（可进相册）；微信等环境再提示长按
    try {
      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/png")
      );
      if (blob) {
        const file = new File(
          [blob],
          `感恩打卡-${cardPost?.post_date || "card"}.png`,
          { type: "image/png" }
        );
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: "感恩打卡",
            text: "我的感恩打卡卡片",
          });
          setCardHint("已打开系统分享，可选择「存储到相册」或发到微信");
          return;
        }
      }
    } catch (err) {
      if (err && err.name === "AbortError") return;
    }

    // 电脑端下载；手机浏览器常无效，故同时给出长按提示
    try {
      const url =
        cardImageUrl || canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = url;
      a.download = `感恩打卡-${cardPost?.post_date || "card"}.png`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      /* ignore */
    }
    setCardHint("手机相册里看不到时：请长按上方图片，选择「保存图片 / 存到相册」");
  };

  const filled = items.filter((x) => (x || "").trim()).length;
  const hasName = Boolean(displayNameInput.trim());
  const displayName =
    displayNameInput.trim() ||
    mine?.display_name ||
    localStorage.getItem("name") ||
    "";
  const displayAvatar =
    mine?.display_avatar || localStorage.getItem("avatar") || "";
  const showMapPromo = Boolean(userId && (justPosted || mine?.item));
  const hasToday = Boolean(mine?.item);

  return (
    <div className="space-y-6">
      <section
        ref={editorRef}
        className="bg-white rounded-2xl border border-amber-100 shadow-sm p-6 md:p-8 space-y-4"
      >
        <div className="space-y-1">
          <h2 className="text-xl font-bold text-gray-800">感恩留言板</h2>
          <p className="text-sm text-gray-500">
            {meta?.hint ||
              "登录后填写姓名即可打卡；可点赞、回复、生成卡片与转发。"}
          </p>
          {meta?.today ? (
            <p className="text-xs text-amber-700">今日日期：{meta.today}</p>
          ) : null}
          {userId && mine?.checkin_day ? (
            <p className="text-xs text-teal-700">
              我的累计打卡：{mine.checkin_day} 天
            </p>
          ) : null}
        </div>

        {!userId ? (
          <div className="rounded-xl bg-amber-50/80 border border-amber-100 p-5 space-y-3 text-center">
            <p className="text-sm text-gray-600">
              请先登录后再打卡（支持<strong>账号登录</strong>或<strong>微信登录</strong>）。
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link
                to={loginRedirect}
                className="px-5 py-2.5 rounded-full bg-rose-600 text-white text-sm"
              >
                账号登录
              </Link>
              <WeChatLoginButton
                redirect="/book-club?tab=gratitude"
                compact
                className="!rounded-full min-w-[140px]"
              />
            </div>
          </div>
        ) : (
          <div
            className={
              showMapPromo
                ? "grid md:grid-cols-[1fr_minmax(220px,280px)] gap-5 items-start"
                : "space-y-3"
            }
          >
            <div className="space-y-4 min-w-0">
              <div className="flex items-center gap-3 text-sm text-gray-600">
                <AuthorAvatar
                  className="w-10 h-10"
                  src={displayAvatar}
                  name={displayName || "?"}
                />
                <div className="min-w-0">
                  <p>
                    {displayName ? (
                      <>
                        以 <strong className="text-gray-800">{displayName}</strong>{" "}
                        打卡
                      </>
                    ) : (
                      <span className="text-amber-800">请先填写姓名再打卡</span>
                    )}
                  </p>
                  {mine?.wechat_nickname ? (
                    <p className="text-xs text-gray-400">
                      微信昵称：{mine.wechat_nickname}（展示以填写的姓名为准）
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="rounded-xl border border-amber-100 bg-amber-50/50 p-3 space-y-2">
                <label className="block text-sm text-gray-700">
                  我的姓名
                  <span className="text-xs text-gray-400 ml-1">（必填）</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  <input
                    value={displayNameInput}
                    onChange={(e) => setDisplayNameInput(e.target.value)}
                    maxLength={40}
                    placeholder="请填写真实姓名或常用称呼"
                    className="flex-1 min-w-[160px] border rounded-xl px-3 py-2 text-sm bg-white"
                  />
                  <button
                    type="button"
                    disabled={nameBusy || !displayNameInput.trim()}
                    onClick={saveName}
                    className="px-4 py-2 rounded-full border border-amber-300 text-amber-900 text-sm disabled:opacity-50"
                  >
                    {nameBusy ? "保存中…" : "保存姓名"}
                  </button>
                </div>
              </div>

              {hasToday && !editing ? (
                <div className="rounded-xl border border-gray-100 bg-gray-50 p-4 space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm font-medium text-gray-800">今日已打卡</p>
                    <button
                      type="button"
                      onClick={startEdit}
                      className="text-sm px-3 py-1.5 rounded-full bg-amber-600 text-white"
                    >
                      修改今日感恩
                    </button>
                  </div>
                  <ol className="list-decimal list-inside text-sm text-gray-700 space-y-1">
                    {(mine.item.items || []).map((it, i) => (
                      <li key={i} className="whitespace-pre-wrap break-words">
                        {it}
                      </li>
                    ))}
                  </ol>
                  <ImageGrid urls={mine.item.images || []} />
                </div>
              ) : (
                <>
                  {hasToday ? (
                    <div className="flex justify-between items-center">
                      <p className="text-sm text-amber-800">正在修改今日感恩</p>
                      <button
                        type="button"
                        className="text-xs text-gray-500 underline"
                        onClick={() => {
                          setEditing(false);
                          loadMine();
                        }}
                      >
                        取消修改
                      </button>
                    </div>
                  ) : null}

                  {items.map((val, idx) => (
                    <div key={idx} className="flex gap-2 items-start">
                      <span className="mt-2.5 text-xs text-amber-600 w-6 shrink-0">
                        {idx + 1}.
                      </span>
                      <div className="flex-1 space-y-1">
                        <textarea
                          value={val}
                          onChange={(e) => updateItem(idx, e.target.value)}
                          maxLength={maxItemLen}
                          rows={2}
                          placeholder={`感恩的事 ${idx + 1}`}
                          className="w-full border rounded-xl px-3 py-2 text-sm resize-y min-h-[2.75rem]"
                        />
                        <p className="text-[11px] text-gray-400 text-right">
                          {(val || "").length}/{maxItemLen}
                        </p>
                      </div>
                      {items.length > MIN_ITEMS ? (
                        <button
                          type="button"
                          onClick={() => removeSlot(idx)}
                          className="mt-1 text-xs text-gray-400 px-2"
                        >
                          删
                        </button>
                      ) : null}
                    </div>
                  ))}

                  <div className="space-y-2">
                    <ImageGrid
                      urls={images}
                      onRemove={(idx) =>
                        setImages((prev) => prev.filter((_, i) => i !== idx))
                      }
                    />
                    <div className="flex flex-wrap gap-2 items-center">
                      <input
                        ref={fileRef}
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={handleUpload}
                      />
                      <button
                        type="button"
                        disabled={uploading || images.length >= maxImages}
                        onClick={() => fileRef.current?.click()}
                        className="text-sm px-3 py-1.5 rounded-full border text-amber-800 disabled:opacity-50"
                      >
                        {uploading
                          ? "压缩并上传中…"
                          : `上传图片（${images.length}/${maxImages}）`}
                      </button>
                      <span className="text-[11px] text-gray-400">
                        最多 {maxImages} 张；过大图会自动缩小后再传
                      </span>
                      {items.length < maxItems ? (
                        <button
                          type="button"
                          onClick={addSlot}
                          className="text-sm px-3 py-1.5 rounded-full border text-amber-800"
                        >
                          + 再加一件（最多 {maxItems}）
                        </button>
                      ) : null}
                      <button
                        type="button"
                        disabled={busy || filled < MIN_ITEMS || !hasName}
                        onClick={submit}
                        className="px-5 py-2.5 rounded-full bg-amber-600 text-white text-sm disabled:opacity-50"
                      >
                        {busy
                          ? "提交中…"
                          : hasToday
                            ? "保存修改"
                            : "今日打卡"}
                      </button>
                      <span className="text-xs text-gray-400">
                        已填 {filled} / 至少 {MIN_ITEMS}
                        {!hasName ? " · 请先填姓名" : ""}
                      </span>
                    </div>
                  </div>
                </>
              )}

              {msg ? <p className="text-sm text-amber-800">{msg}</p> : null}
              {justPosted ? (
                <p className="text-xs text-teal-800 bg-teal-50 border border-teal-100 rounded-lg px-3 py-2">
                  打卡成功。右侧可去地图标识位置，也可玩漂流瓶。
                </p>
              ) : null}
            </div>

            {showMapPromo ? <GratitudePlanetMapPromo /> : null}
          </div>
        )}
      </section>

      <section className="bg-white rounded-2xl border shadow-sm p-6 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-semibold text-gray-800">打卡动态</h3>
          <div className="flex rounded-full border border-gray-200 p-0.5 text-sm">
            <button
              type="button"
              onClick={() => switchFeedScope("all")}
              className={`px-3.5 py-1.5 rounded-full transition ${
                feedScope === "all"
                  ? "bg-amber-600 text-white"
                  : "text-gray-600 hover:text-gray-800"
              }`}
            >
              全部
            </button>
            <button
              type="button"
              onClick={() => switchFeedScope("mine")}
              className={`px-3.5 py-1.5 rounded-full transition ${
                feedScope === "mine"
                  ? "bg-amber-600 text-white"
                  : "text-gray-600 hover:text-gray-800"
              }`}
            >
              我的
            </button>
          </div>
        </div>
        {loading ? (
          <p className="text-sm text-gray-400">加载中…</p>
        ) : feed.length === 0 ? (
          <p className="text-sm text-gray-400">
            {feedScope === "mine"
              ? "你还没有打卡记录，先写今日感恩吧"
              : "还没有打卡，来写第一条吧"}
          </p>
        ) : (
          <ul className="space-y-4">
            {feed.map((p) => {
              const isMine = userId && p.user_id === userId;
              const comments = openComments[p.id];
              const likers = p.recent_likers || [];
              return (
                <li
                  key={p.id}
                  id={`gratitude-post-${p.id}`}
                  className="border border-gray-100 rounded-xl p-4 space-y-3"
                >
                  <div className="flex gap-3">
                    <AuthorAvatar src={p.author_avatar} name={p.author_name} />
                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="flex flex-wrap items-baseline gap-2">
                        <span className="font-medium text-gray-800">
                          {p.author_name}
                        </span>
                        <span className="text-xs text-gray-400">
                          第{p.checkin_day || 1}天打卡
                        </span>
                        <span className="text-xs text-gray-400">
                          {p.checkin_at ||
                            (p.checkin_time
                              ? `${p.post_date} ${p.checkin_time}`
                              : p.post_date)}
                        </span>
                        {isMine ? (
                          <button
                            type="button"
                            onClick={startEdit}
                            className="text-[11px] text-rose-600 underline"
                          >
                            修改
                          </button>
                        ) : null}
                      </div>
                      <ol className="list-decimal list-inside text-sm text-gray-700 space-y-1">
                        {(p.items || []).map((it, i) => (
                          <li key={i} className="whitespace-pre-wrap break-words">
                            {it}
                          </li>
                        ))}
                      </ol>
                      <ImageGrid urls={p.images || []} />

                      <div className="flex flex-wrap items-center gap-1 pt-1 border-t border-gray-50">
                        <ActionIcon
                          label="点赞"
                          active={p.liked_by_me}
                          count={p.like_count}
                          onClick={() => toggleLike(p)}
                        >
                          <svg
                            viewBox="0 0 24 24"
                            className="w-4 h-4"
                            fill={p.liked_by_me ? "currentColor" : "none"}
                            stroke="currentColor"
                            strokeWidth="1.8"
                          >
                            <path d="M12 21s-7-4.5-9.5-8.5C.5 9 2.5 5.5 6 5.5c1.9 0 3.3 1.1 4 2.1.7-1 2.1-2.1 4-2.1 3.5 0 5.5 3.5 3.5 7C19 16.5 12 21 12 21z" />
                          </svg>
                        </ActionIcon>
                        <ActionIcon
                          label="回复"
                          count={p.reply_count}
                          onClick={() => toggleComments(p)}
                        >
                          <svg
                            viewBox="0 0 24 24"
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.8"
                          >
                            <path d="M4 6h16v10H8l-4 3V6z" />
                          </svg>
                        </ActionIcon>
                        <ActionIcon label="生成卡片" onClick={() => setCardPost(p)}>
                          <svg
                            viewBox="0 0 24 24"
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.8"
                          >
                            <rect x="4" y="5" width="16" height="14" rx="2" />
                            <path d="M8 10h8M8 14h5" />
                          </svg>
                        </ActionIcon>
                        <ActionIcon
                          label="转发"
                          count={p.share_count}
                          onClick={() => sharePost(p)}
                        >
                          <svg
                            viewBox="0 0 24 24"
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.8"
                          >
                            <path d="M14 7l5 5-5 5M19 12H9a5 5 0 0 0-5 5" />
                          </svg>
                        </ActionIcon>
                      </div>

                      {likers.length > 0 ? (
                        <div className="rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600 flex items-center gap-1.5">
                          <span className="text-rose-500" aria-hidden>
                            ♥
                          </span>
                          <span>
                            {likers.map((l) => l.name).join("、")}
                            {p.like_count > likers.length ? " 等" : ""} 赞了
                          </span>
                        </div>
                      ) : null}

                      {comments?.open ? (
                        <div className="rounded-lg bg-stone-50 border border-stone-100 p-3 space-y-2">
                          {(comments.items || []).length === 0 ? (
                            <p className="text-xs text-gray-400">还没有回复，来第一条吧</p>
                          ) : (
                            <ul className="space-y-1.5">
                              {comments.items.map((c) => (
                                <li key={c.id} className="text-sm text-gray-700">
                                  <span className="font-medium text-gray-800">
                                    {c.author_name}
                                  </span>
                                  <span className="text-gray-400"> · </span>
                                  {c.content}
                                </li>
                              ))}
                            </ul>
                          )}
                          {userId ? (
                            <div className="flex gap-2 pt-1">
                              <input
                                value={commentDrafts[p.id] || ""}
                                onChange={(e) =>
                                  setCommentDrafts((prev) => ({
                                    ...prev,
                                    [p.id]: e.target.value.slice(0, maxCommentLen),
                                  }))
                                }
                                maxLength={maxCommentLen}
                                placeholder="写一句回复…"
                                className="flex-1 border rounded-lg px-2.5 py-1.5 text-sm"
                              />
                              <button
                                type="button"
                                disabled={
                                  commentBusy[p.id] ||
                                  !(commentDrafts[p.id] || "").trim()
                                }
                                onClick={() => submitComment(p.id)}
                                className="px-3 py-1.5 rounded-lg bg-amber-600 text-white text-sm disabled:opacity-50"
                              >
                                发送
                              </button>
                            </div>
                          ) : (
                            <p className="text-xs text-gray-500">
                              <Link to={loginRedirect} className="text-rose-600 underline">
                                登录
                              </Link>
                              后可回复
                            </p>
                          )}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {cardPost ? (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={closeCardModal}
        >
          <div
            className="bg-white rounded-t-2xl sm:rounded-2xl p-4 max-w-sm w-full space-y-3 shadow-xl max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-gray-800">感恩打卡卡片</h4>
              <button
                type="button"
                className="text-gray-400 text-sm"
                onClick={closeCardModal}
              >
                关闭
              </button>
            </div>
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
              手机请<strong>长按整张卡片</strong>保存到相册；或直接<strong>长按右下角二维码区域</strong>识别进感恩留言板。
            </p>
            {/* 绘制用；展示用 img，才能长按存相册 / 识别二维码 */}
            <canvas ref={cardCanvasRef} className="hidden" width={900} height={1280} />
            {cardImageUrl ? (
              <img
                src={cardImageUrl}
                alt="感恩打卡卡片"
                className="w-full rounded-xl border bg-slate-100 select-none"
                style={{ WebkitTouchCallout: "default", WebkitUserSelect: "auto" }}
              />
            ) : (
              <div
                className="w-full rounded-xl border bg-slate-100 flex items-center justify-center text-sm text-gray-400"
                style={{ aspectRatio: "900 / 1280" }}
              >
                生成中…
              </div>
            )}
            {cardHint ? (
              <p className="text-xs text-center text-gray-500">{cardHint}</p>
            ) : null}
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                disabled={!cardReady}
                onClick={downloadCard}
                className="py-2.5 rounded-full bg-amber-600 text-white text-sm disabled:opacity-50"
              >
                {cardReady ? "保存 / 分享" : "生成中…"}
              </button>
              <button
                type="button"
                onClick={closeCardModal}
                className="py-2.5 rounded-full border text-gray-600 text-sm"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
